import { AlertCircle } from 'lucide-react';
import { forwardRef, type ReactNode } from 'react';
import { cn } from 'v5/platform/dom/cn';

export type FullPageStatusLayoutProps = {
  idPrefix: string;
  title: string;
  description: string;
  className?: string;
  children?: ReactNode;
  actions?: ReactNode;
};

export const FullPageStatusLayout = forwardRef<
  HTMLDivElement,
  FullPageStatusLayoutProps
>(function FullPageStatusLayout(
  { idPrefix, title, description, className, children, actions },
  ref
) {
  const titleId = `${idPrefix}-title`;
  const descriptionId = `${idPrefix}-description`;

  return (
    <div ref={ref} className="tailwind-root">
      <div
        className={cn(
          'flex min-h-screen flex-col items-center justify-center gap-3 bg-gradient-to-br from-slate-50 to-slate-200 px-4 text-center',
          className
        )}
      >
        <AlertCircle
          className="mb-3 size-20 text-fg-danger animate-bounce"
          aria-hidden="true"
        />
        <h1
          id={titleId}
          role="alert"
          aria-describedby={descriptionId}
          tabIndex={-1}
          autoFocus
          className="mb-2 text-3xl font-bold text-fg-primary drop-shadow outline-none md:text-4xl"
        >
          {title}
        </h1>
        <p
          id={descriptionId}
          className="mx-auto mb-5 max-w-180 text-lg text-fg-secondary md:text-xl"
        >
          {description}
        </p>

        {children}

        {actions ? (
          <div className="flex flex-wrap items-center justify-center gap-3">
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
});

FullPageStatusLayout.displayName = 'FullPageStatusLayout';
